import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useNotification } from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { EventGroupForm } from "@/pages/event-groups/components/event-group-form";
import { BackLink } from "@/pages/event-groups/components/group-page-states";
import { useEventGroupRepository } from "@/pages/event-groups/hooks/use-event-group-repository";
import { refusalMessage } from "@/pages/event-groups/lib/refusal-message";
import type { EventGroupFormType } from "@/pages/event-groups/schemas/event-group.form";

/**
 * Makes a group, then opens its edit page, where the events are added. The group needs a photo
 * before the first save, so the photo is uploaded under an id made here: the backend makes the
 * group's own id, and the storage id is never sent.
 */
export const EventGroupCreate = () => {
  const repo = useEventGroupRepository();
  const navigate = useNavigate();
  const { open } = useNotification();
  const setTitle = useDocumentTitle();
  const [storageId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    setTitle("Create event group");
  }, []);

  async function save({ name, about, media }: EventGroupFormType) {
    try {
      const created = await repo.create({ name, about, media });
      open?.({
        type: "success",
        message: `Created ${created.name}`,
        description: "Add its events next.",
      });
      navigate(`/event-groups/edit/${created.id}`);
      return true;
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to create the group",
        description: refusalMessage(error),
      });
      return false;
    }
  }

  return (
    <div className="p-6 pb-24">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <BackLink />
        <h1 className="text-2xl font-bold">Create event group</h1>
        <EventGroupForm
          // An empty media value, not none: with none the form's error reads "Required", and with
          // no photo in it the error names the photo.
          defaultValues={{ id: storageId, media: { photos: [], teasers: [] } }}
          submitLabel="Create group"
          onSave={save}
        />
      </div>
    </div>
  );
};
